import React, { useMemo, useCallback } from "react";
import { FeatureFlags } from "../../constants/LocalServerConstants";
import { LmsFormDropdownField, LmsFormField } from "./LmsFormGenerator";
import { Button, Checkbox, Dropdown, Icon, Table, Image } from "semantic-ui-react";

const RowTitle = ({ rowTitle, i }) => {
  if (!rowTitle) {
    return null;
  }

  return <Table.Cell>{`${rowTitle} ${i + 1}`}</Table.Cell>;
};

const LmsTableRow = (
  {
    rowData,
    i,
    props,
    filteredData,
    setSortedData,
    checkedItems,
    handleCheckbox,
    checkbox,
    skipCheckbox,
    isDisabled,
    currentViewGroups,
    rowTitle,
    columns,
    getClickOptions,
    handleActionClick,
    handlePush,
    activeColumn,
    isEditable,
    selectable,
    myForm,
    editableFields,
    yesNoDropdownOptions,
    handleFormat,
    allDropdownOptionsData,
    allDropdownOptionsLoading,
    trueFalseDict,
    handleChange,
    errors,
    inputs,
    getEditableValue,
    handleDelete,
    isBrokerColor,
    isUpward,
    brokerTableEllipsesOverrideCSS
  }) => {
  const checked = checkedItems?.includes(i);
  const isSummary = !(props.disableSummary && ["New", "Sent"].indexOf(rowData.Status) > -1);
  const isApplication = !(props.disableApplication && ["Revision", "Submitted"].indexOf(rowData.Status) > -1);
  const rowsBackgroundCustomColor = props.rowsBackgroundCustomColor ? props.rowsBackgroundCustomColor(rowData) : "";
  const rowBackgroundColor = props.rowBackgroundColor && props?.rowBackgroundColorConditionFunc ? props?.rowBackgroundColorConditionFunc(rowData) : "";
  const rowStyles = useMemo(() => {
    if (rowsBackgroundCustomColor) {
      return { backgroundColor: rowsBackgroundCustomColor };
    }
    else if (rowBackgroundColor) {
      return { backgroundColor: props.rowBackgroundColor };
    }
    else if (props.rowBackgroundDefaultColor) {
      return { backgroundColor: props.rowBackgroundDefaultColor };
    }
  }, [props.rowBackgroundDefaultColor, rowsBackgroundCustomColor, rowBackgroundColor]);

  const downloadFileName = useCallback((prop) => {
    if (props.onClickDownloadFileNameProp && filteredData?.[i]?.[props.onClickDownloadFileNameProp]) {
      return filteredData?.[i]?.[props.onClickDownloadFileNameProp];
    }
    else if (props.columnClickOptions?.[prop]?.downloadFileName) {
      return props.columnClickOptions?.[prop]?.downloadFileName;
    }
    else {
      return props.onClickDownloadFileName || filteredData?.[i]?.[prop] || "";
    }
  }, [filteredData, i, columns, props.columnClickOptions, props.onClickDownloadFileName]);

  return <Table.Row key={`table-row-${i}`} style={rowStyles}>
      {currentViewGroups && currentViewGroups.length > 0 && <>
        {currentViewGroups.map((_, index) => {
          return <Table.Cell className="lms-table-row-blank-indent-column" key={`group-indent-space-${index}`}></Table.Cell>;
        })}
      </>}
      {(checkbox && !skipCheckbox && !isDisabled) && <Table.Cell className="table-checkbox">
        <Checkbox onChange={(e, data) => handleCheckbox(i, data?.checked, rowData)} checked={checked} className="lms-checkbox" />
      </Table.Cell>}
      <RowTitle rowTitle={rowTitle} i={i} />
      {columns?.map(({ prop, fieldType, numberFormat }, j) => {
        const { itemId, isClickable, isLink, params } = getClickOptions(rowData, prop, i);
        const editProp = `${itemId}_${i}`;
        const getEditableStyle = (fieldErrors, isEdited, editableField) => {
          let cellEditableStyle = "lms-table-editable-cell";

          if (editableField?.field === "checkbox") {
            cellEditableStyle = `${cellEditableStyle} lms-table-yes-no-dropdown-style`;
          }
          if (fieldErrors) {
            cellEditableStyle = `${cellEditableStyle} lms-table-error-cell`;
          }
          if (isEdited) {
            cellEditableStyle = `${cellEditableStyle} lms-table-edited-cell`;
          }
          if (editableField?.fieldType === "percent") {
            cellEditableStyle = `${cellEditableStyle} lms-table-percent-input`;
          }
          if (editableField?.fieldType === "dollar") {
            cellEditableStyle = `${cellEditableStyle} lms-table-dollar-input`;
          }

          return cellEditableStyle;
        };

        const clickPath = props.columnClickOptions?.[prop]?.path || props.onClickPath;
        const path = typeof clickPath === "function" ? clickPath(rowData, prop, i) : clickPath;
        const clickOptions = {
          clickedRow: filteredData[i],
          id: props.columnClickOptions?.[prop]?.downloadHasData || props.onClickDownloadHasData ? undefined : itemId,
          path,
          params,
          downloadPath: props.columnClickOptions?.[prop]?.downloadPath || props.onClickDownloadPath,
          downloadMethod: props.columnClickOptions?.[prop]?.downloadMethod || props.onClickDownloadMethod,
          downloadPayloadContentType: props.columnClickOptions?.[prop]?.downloadPayloadContentType || props.onClickDownloadPayloadContentType,
          data: props.columnClickOptions?.[prop]?.downloadHasData || props.onClickDownloadHasData ? [itemId] : undefined,
          fileName: downloadFileName(prop),
          isModalContentLink: props.columnClickOptions?.[prop]?.isModalContentLink,
          isPopupLink: props?.isPopupLink || props.columnClickOptions?.[prop]?.isPopupLink,
          isIframeModal: props?.isIframeModal || props.columnClickOptions?.[prop]?.isIframeModal,
          isClickable: isClickable,
          tableData: filteredData,
          checkedItems,
          setTableData: setSortedData,
          handleActionClick,
          handlePush,
          isRowClick: true
        };

        const isCursorPointer = selectable && !props.skipClick && !isEditable && (!isDisabled || (isDisabled && (props.handleClick || props.isDisableClickable))) && activeColumn.index === undefined;
        const isNotEditableCell = !myForm || !editableFields[prop] || !isEditable;
        const formattedValue = isNotEditableCell ? handleFormat(rowData[prop], prop, props.dataRules, props.currencyFields, { fieldType, numberFormat }) : "";
        const cellStyles = {} as any;
        if (isLink) {
          cellStyles.textDecoration = "underline";
        }
        else if (isNotEditableCell && typeof rowData[prop] === "number" && rowData[prop] < 0 && typeof formattedValue === "string" && formattedValue?.startsWith("$")) {
          cellStyles.color = "red";
        }

        return (
          <Table.Cell
            key={`table-cell-${j}`}
            className={isCursorPointer ? `${isClickable ? "cursor-pointer " : ""}lms-table-cell-padding` : isEditable ? "lms-table-cell-no-padding" : props.compact || props.isDisabled ? "" : "lms-table-cell-padding"}
            onClick={() => {
              if ((!myForm || !isEditable) && activeColumn.index === undefined) {
                if (props.handleClick) {
                  props.handleClick(clickOptions, j, prop);
                }
                else {
                  if ((!props.skipClick && !isDisabled) || (isDisabled && props.isDisableClickable)) {
                    handleActionClick(clickOptions).catch(e => {
                      if (FeatureFlags.IS_DEBUG) {
                        console.log(e);
                      }
                    });
                  }
                }
              }
            }}
          >{(!myForm || !editableFields[prop] || !isEditable) && (
            <span style={cellStyles}>{formattedValue}</span>
          )}
            {(isEditable && editableFields[prop] && editableFields[prop].field && (editableFields[prop].field === "dropdown" || editableFields[prop].field === "checkbox")) &&
              <React.Fragment>
                <LmsFormDropdownField
                  options={editableFields[prop].field === "checkbox" ? {
                    ...editableFields[prop],
                    dropdownOptions: yesNoDropdownOptions,
                    field: "dropdown"
                  } : editableFields[prop]}
                  dropdownOptionsData={allDropdownOptionsData}
                  isLoading={allDropdownOptionsLoading}
                  errors={errors[editProp]}
                  handleChange={(e, {
                    value,
                    name
                  }, type) => handleChange(e, {
                    value: editableFields[prop].field === "checkbox" && trueFalseDict[value] !== undefined ? trueFalseDict[value] : value,
                    name
                  }, type, editProp)}
                  name={editableFields[prop].tableValueName || editableFields[prop].name || prop}
                  value={getEditableValue(
                    editableFields[prop].tableValueName || editableFields[prop].name,
                    editProp,
                    inputs,
                    rowData[editableFields[prop].tableValueName || editableFields[prop].name]
                  )?.value as any}
                  fieldClassName={getEditableStyle(
                    errors[editProp] ? errors[editableFields[prop].tableValueName || editableFields[prop].name] : null,
                    getEditableValue(
                      editableFields[prop].tableValueName || editableFields[prop].name,
                      editProp,
                      inputs,
                      rowData[editableFields[prop].tableValueName || editableFields[prop].name]
                    ).isEdited,
                    editableFields[prop]
                  )}
                  isPromiseAll
                  skipLabel
                  hideErrorMessage
                  upward={isUpward(i)}
                  isTableInline
                />
              </React.Fragment>
            }
            {(isEditable && editableFields[prop] && editableFields[prop].field && (editableFields[prop].field !== "dropdown" && editableFields[prop].field !== "checkbox")) &&
              <div className={editableFields[prop].field === "textarea" ? "ui form" : ""}>
                <LmsFormField
                  options={editableFields[prop]}
                  errors={errors[editProp]}
                  handleChange={(e, { name, value }, type) => handleChange(e, { name, value: value || "" }, type, editProp)}
                  value={getEditableValue(prop, editProp, inputs, rowData[prop]).value as any}
                  fieldClassName={getEditableStyle(errors[editProp] ? errors[editProp][prop] : null, getEditableValue(prop, editProp, inputs, rowData[prop]).isEdited, editableFields[prop])}
                  skipLabel
                  hideErrorMessage
                  isTableInline
                />
              </div>
            }
          </Table.Cell>
        );
      })}
      {props.actions && <Table.Cell>
        {props.actions.includes("invite") && <Button
          className="table-button"
          icon
          color="green"
          inverted
          onClick={() => props.handleTableInvite(i)}
        >
          <div className="gmr-button-container">
            {props.isMobile ? null :
              <div className="table-button-text">{props.buttonWords && props.buttonWords.length > 0 ? props.buttonWords[i] : "Invite Now"}</div>}
          </div>
        </Button>}
        {(props.actions.includes("edit") && !rowData.isEditHide) && <Button
          className="table-button"
          icon
          color="green"
          inverted
          onClick={() => props.handleTableEdit(i)}
        >
          <div className="gmr-button-container">
            <Icon name='edit' />
            {props.isMobile ? null : <div className="table-button-text">Edit</div>}
          </div>
        </Button>}
        {(props.actions.includes("delete") && !props.isDeleteHide && !rowData.isDeleteHide) && <Button
          className="table-button"
          icon
          color="red"
          inverted
          onClick={() => props.handleTableDelete ? props.handleTableDelete(i) : handleDelete(i)}
        >
          <div className="gmr-button-container">
            <Icon name='trash' />
            {props.isMobile ? null : <div className="table-button-text">Delete</div>}
          </div>
        </Button>}
      </Table.Cell>}
      {props.ellipsis && <Table.Cell textAlign='right'>
        <Image floated='right'>
          <Dropdown key={"card-dropdown"} className={isBrokerColor ? brokerTableEllipsesOverrideCSS : "icon landing-page-card-header-icon"}
                    icon="ellipsis horizontal"
                    pointing='right'>
            <Dropdown.Menu key={"card-menu"}>
              {props.handleExport && <Dropdown.Item key={"export-quote"} onClick={() => props.handleExport(rowData.id)}>Export Quote</Dropdown.Item>}
              {props.handleGMR && <Dropdown.Item key={"edit-gmr"} onClick={() => props.handleGMR(rowData)}>Edit Get My Rate</Dropdown.Item>}
              {props.handleGMR && <Dropdown.Item key={"copy-gmr"} onClick={() => props.handleGMR(rowData, true)}>Copy Get My Rate</Dropdown.Item>}
              {(props.handleApplication && isApplication) &&
                <Dropdown.Item key={"edit-application"} onClick={() => props.handleApplication(rowData)}>Edit Application</Dropdown.Item>}
              {props.handleCopyApplication &&
                <Dropdown.Item key={"copy-application"} onClick={() => props.handleCopyApplication(rowData)}>Copy Application</Dropdown.Item>}
              {(props.handleSummary && isSummary) && <Dropdown.Item key={"view-summary"} onClick={() => props.handleSummary(rowData.id)}>View Summary</Dropdown.Item>}
              {props.ellipsisHandlers && props.ellipsisHandlers.map((ellipsisHandler, j) => {
                return (
                  <Dropdown.Item
                    key={`ellipsisHandler-${j}`}
                    disabled={!props.editMode}
                    onClick={() => ellipsisHandler.action(i)}
                  >{ellipsisHandler.text}</Dropdown.Item>
                );
              })}
            </Dropdown.Menu>
          </Dropdown>
        </Image>
      </Table.Cell>}
  </Table.Row>;
};

export default LmsTableRow;